import { UserAvatar } from "@/components/ai/chat/UserAvatar";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, type FC } from "react";
import { v4 as uuidv4 } from "uuid";

export type NewPendingMessage = {
    id: string;
    // If content is undefined, it means the message with `id` failed to be sent
    // and should be retracted again.
    content?: string;
};

export const ChatInput: FC<{
    agentExecution: PlainAgentExecution;
    onNewMessage: (message: NewPendingMessage) => void;
}> = ({ agentExecution, onNewMessage }) => {
    const sendToAgentExecution = useSendToAgentExecution();
    const { toast } = useToast();

    const onSubmit = useCallback(
        async (text: string) => {
            if (!agentExecution.id) {
                return;
            }
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecution.id,
                    userInput: new UserInputBlock({
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: text,
                            }),
                        },
                    }),
                }),
            );
        },
        [agentExecution.id, sendToAgentExecution],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                const value = input.value;
                const id = uuidv4();
                try {
                    input.value = ""; // Clear input to provide quick feedback
                    onNewMessage({ id, content: value });
                    await onSubmit(value);
                } catch (error) {
                    input.value = value; // Reset value if it failed
                    onNewMessage({ id, content: undefined });
                    toast({
                        title: "Error sending to agent execution",
                        description: formatError(error),
                    });
                }
                return;
            }
        },
        [onSubmit, toast, onNewMessage],
    );

    return (
        <div className="flex w-full items-start gap-2 rounded-xl border border-border-base bg-surface-pure p-4 drop-shadow-[0_20px_20px_rgba(0,0,0,0.10)]">
            <UserAvatar className="mt-[9px]" />
            <Textarea
                maxRows={15}
                placeholder="Reply or interrupt Ona"
                onKeyDown={onKeyDown}
                className="max-w-full resize-none bg-surface-pure"
                data-testid="agent-execution-chat-input"
            />
        </div>
    );
};
